import React, { useMemo, useState } from "react";
import { Loader2, Plus, Trash2, Search, X } from "lucide-react";
import type { Mcc } from "../../../features/purposeCode/PurposeCodeSlice";
import MccMappingAddSection from "./MccMappingAddSection";

interface PurposeMappingsModalContentProps {
  loading: boolean;
  error?: string | null;
  mccs: Record<string, string>;
  purposeCode: string;
  allMccs: Mcc[];
  mappingUpdateLoading: boolean;
  mappingUpdateError?: string | null;
  onAddMapping: (mcc: string) => void | Promise<void>;
  onRemoveMapping: (mcc: string) => void | Promise<void>;
}

const PurposeMappingsModalContent: React.FC<PurposeMappingsModalContentProps> = ({
  loading,
  error,
  mccs,
  purposeCode,
  allMccs,
  mappingUpdateLoading,
  mappingUpdateError,
  onAddMapping,
  onRemoveMapping,
}) => {
  const [search, setSearch] = useState("");
  const [showAddSection, setShowAddSection] = useState(false);
  const [pendingRemoveMcc, setPendingRemoveMcc] = useState<string | null>(null);

  const mccRows = useMemo(
    () =>
      Object.entries(mccs).map(([mcc, description]) => ({
        mcc,
        description: description || "",
      })),
    [mccs],
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return mccRows;
    return mccRows.filter(
      (row) =>
        row.mcc.toLowerCase().includes(term) ||
        row.description.toLowerCase().includes(term),
    );
  }, [mccRows, search]);

  return (
    <div className="w-full overflow-y-auto max-h-[70vh]">
      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading mappings...
        </div>
      ) : error ? (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {error}
        </div>
      ) : (
        <div className="px-1 sm:px-2 py-1">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-[11px] text-gray-500">
              These are the MCC codes currently mapped to this purpose code.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowAddSection(open => !open);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-cardborder bg-white text-gray-800 hover:bg-gray-50"
            >
              <Plus className="w-3.5 h-3.5" />
              {showAddSection ? "Cancel new mapping" : "Add MCC mapping"}
            </button>
          </div>

          {showAddSection && (
            <div className="mb-4">
              <MccMappingAddSection
                purposeCode={purposeCode}
                allMccs={allMccs}
                existingMappings={mccs}
                mappingUpdateLoading={mappingUpdateLoading}
                mappingUpdateError={mappingUpdateError}
                onAddMapping={onAddMapping}
              />
            </div>
          )}

          <div className="relative mb-4">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter MCC or description"
              className="w-full pl-9 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none"
            />
            {!!search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear filter"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
            <span>
              Showing {filteredRows.length} of {mccRows.length} MCC mapping
              {mccRows.length === 1 ? "" : "s"}
            </span>
            <span>
              Total mapped MCCs:{" "}
              <span className="font-semibold text-gray-700">{mccRows.length}</span>
            </span>
          </div>
          <div className="max-h-[360px] overflow-y-auto overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-left border-b border-gray-200 text-gray-600">
                  <th className="py-3.5 px-4 font-medium">MCC</th>
                  <th className="py-3.5 pr-4 font-medium">Description</th>
                  <th className="py-3.5 pr-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.mcc} className="border-b border-gray-100">
                    <td className="py-3.5 px-4 font-medium text-gray-800">{row.mcc}</td>
                    <td className="py-3.5 pr-4 text-gray-700">{row.description || "-"}</td>
                    <td className="py-3.5 pr-4 text-right">
                      <button
                        type="button"
                        onClick={() => setPendingRemoveMcc(row.mcc)}
                        disabled={mappingUpdateLoading}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-60"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {!filteredRows.length && (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-gray-500">
                      {search ? "No MCC mappings match your filter" : "No MCC mappings found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pendingRemoveMcc && (
            <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
              <div className="bg-white rounded-xl shadow-lg max-w-sm w-full mx-4 p-6 sm:p-7">
                <h3 className="text-base font-semibold text-gray-900">
                  Remove MCC mapping
                </h3>
                <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                  Are you sure you want to remove MCC{" "}
                  <span className="font-semibold">{pendingRemoveMcc}</span> from purpose code{" "}
                  <span className="font-semibold">{purposeCode}</span>?
                </p>
                <div className="mt-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setPendingRemoveMcc(null)}
                    className="px-3.5 py-1.5 text-xs rounded-md border border-cardborder text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={mappingUpdateLoading}
                    onClick={() => {
                      if (!pendingRemoveMcc) return;
                      onRemoveMapping(pendingRemoveMcc);
                      setPendingRemoveMcc(null);
                    }}
                    className="px-3.5 py-1.5 text-xs rounded-md border border-red-600 bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    Remove mapping
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PurposeMappingsModalContent;
